import type { LoaderFunctionArgs } from 'react-router-dom'

import { DurationTestStatus, TransferState } from '@/api/definition'
import { samApiClient } from '@/api/index'
import type { Device } from '@/pages/Device/Device'

// Define loader data interfaces
export interface DurationTestLoaderData {
  initialDevices: Device[]
  devicesInTest: Device[]
  testResults: unknown[]
  rowCount: number
  error?: string
}

export interface StartTestLoaderData {
  availableDevices: Device[]
  rowCount: number
  error?: string
}

export interface StopTestLoaderData {
  devicesInTest: Device[]
  rowCount: number
  error?: string
}

export interface TestResultsLoaderData {
  testResults: unknown[]
  rowCount: number
  error?: string
}

// Main duration test loader
export async function durationTestLoader(
  _args: LoaderFunctionArgs,
): Promise<DurationTestLoaderData> {
  try {
    // Fetch devices available for duration test - using V1 format
    const availableDevicesParams = new URLSearchParams({
      page: '1',
      per_page: '50',
      not_in_service: '0', // inService: true
      self_test: '1',
      duration_test_status:
        DurationTestStatus.DurationTestNotStarted.toString(),
      transfer_status: TransferState.DOMESTIC.toString(),
    })

    const availableDevicesResponse = await samApiClient.get(
      `device?${availableDevicesParams.toString()}`,
    )

    // Fetch devices currently in duration test - using V1 format
    const devicesInTestParams = new URLSearchParams({
      page: '1',
      per_page: '50',
      not_in_service: '0', // inService: true
      self_test: '0',
      duration_test_status:
        DurationTestStatus.DurationTestInProgress.toString(),
    })

    const devicesInTestResponse = await samApiClient.get(
      `duration-test-reports?${devicesInTestParams.toString()}`,
    )

    // Fetch test results (completed/verified tests) - using V1 format
    const testResultsParams = new URLSearchParams({
      page: '1',
      per_page: '50',
      not_in_service: '0', // inService: true
      self_test: '0',
      duration_test_status: DurationTestStatus.DurationTestVerify.toString(),
    })

    const testResultsResponse = await samApiClient.get(
      `duration-test-reports?${testResultsParams.toString()}`,
    )

    // V1 format returns data directly, not wrapped in a data property
    const availableDevicesData = availableDevicesResponse.data ?? []
    const devicesInTestData = devicesInTestResponse.data ?? []
    const testResultsData = testResultsResponse.data ?? []

    // Get row count from headers (V1 format)
    const availableDevicesCount =
      Number(availableDevicesResponse.headers['x-total-count']) || 0

    // Transform data
    const transformedAvailableDevices = availableDevicesData?.length
      ? availableDevicesData.map((device: Device) => ({
          ...device,
          id: device.uid,
        }))
      : []

    const transformedDevicesInTest = devicesInTestData?.length
      ? devicesInTestData.map((device: Device) => ({
          ...device,
          id: device.uid,
        }))
      : []

    const transformedTestResults = testResultsData?.length
      ? testResultsData.map((device: Device) => ({
          ...device,
          id: device.uid,
        }))
      : []

    return {
      initialDevices: transformedAvailableDevices,
      devicesInTest: transformedDevicesInTest,
      testResults: transformedTestResults,
      rowCount: availableDevicesCount,
    }
  } catch (error) {
    console.error('Failed to load duration test data:', error)
    return {
      initialDevices: [],
      devicesInTest: [],
      testResults: [],
      rowCount: 0,
      error: 'Failed to load duration test data',
    }
  }
}

// Loader for start test tab
export async function startTestLoader(
  _args: LoaderFunctionArgs,
): Promise<StartTestLoaderData> {
  try {
    // Using V1 format - GET with query parameters
    const params = new URLSearchParams({
      page: '1',
      per_page: '10',
      not_in_service: '0', // inService: true
      self_test: '1',
      duration_test_status:
        DurationTestStatus.DurationTestNotStarted.toString(),
      transfer_status: TransferState.DOMESTIC.toString(),
    })

    const response = await samApiClient.get(`device?${params.toString()}`)

    // V1 format returns data directly
    const data = response.data ?? []
    const rowCount = Number(response.headers['x-total-count']) || 0

    const transformedData = data?.length
      ? data.map((device: Device) => ({
          ...device,
          id: device.uid,
        }))
      : []

    return {
      availableDevices: transformedData,
      rowCount,
    }
  } catch (error) {
    console.error('Failed to load devices for duration test:', error)
    return {
      availableDevices: [],
      rowCount: 0,
      error: 'Failed to load devices for duration test',
    }
  }
}

// Loader for stop test tab
export async function stopTestLoader(
  _args: LoaderFunctionArgs,
): Promise<StopTestLoaderData> {
  try {
    // Using V1 format - GET with query parameters
    const params = new URLSearchParams({
      page: '1',
      per_page: '10',
      not_in_service: '0', // inService: true
      self_test: '0',
      duration_test_status:
        DurationTestStatus.DurationTestInProgress.toString(),
    })

    const response = await samApiClient.get(
      `duration-test-reports?${params.toString()}`,
    )

    // V1 format returns data directly
    const data = response.data ?? []
    const rowCount = Number(response.headers['x-total-count']) || 0

    const transformedData = data?.length
      ? data.map((device: Device) => ({
          ...device,
          id: device.uid,
        }))
      : []

    return {
      devicesInTest: transformedData,
      rowCount,
    }
  } catch (error) {
    console.error('Failed to load devices in duration test:', error)
    return {
      devicesInTest: [],
      rowCount: 0,
      error: 'Failed to load devices in duration test',
    }
  }
}

// Loader for test results tab
export async function testResultsLoader(
  _args: LoaderFunctionArgs,
): Promise<TestResultsLoaderData> {
  try {
    const params = new URLSearchParams({
      page: '1',
      per_page: '100',
      not_in_service: '0', // inService: true
      self_test: '0',
      duration_test_status: DurationTestStatus.DurationTestVerify.toString(),
    })

    const response = await samApiClient.get(
      `duration-test-reports?${params.toString()}`,
    )

    const data = response.data ?? []
    const rowCount = Number(response.headers['x-total-count']) || 0

    return {
      testResults: data,
      rowCount,
    }
  } catch (error) {
    console.error('Failed to load duration test results:', error)
    return {
      testResults: [],
      rowCount: 0,
      error: 'Failed to load duration test results',
    }
  }
}

// Function to fetch duration test count for badge - using V1 format
export const fetchDurationTestCount = async (): Promise<number> => {
  try {
    // Use V1 format - get count from device endpoint with small page size
    const params = new URLSearchParams({
      page: '1',
      per_page: '1', // Just need count, not data
      not_in_service: '0', // inService: true
      self_test: '0',
      duration_test_status:
        DurationTestStatus.DurationTestInProgress.toString(),
    })

    const response = await samApiClient.get(
      `duration-test-reports?${params.toString()}`,
    )
    return Number(response.headers['x-total-count']) || 0
  } catch (error) {
    console.error('Failed to fetch duration test count:', error)
    return 0
  }
}

// Function to fetch finish (verify) duration test count for badge - V1 format
export const fetchDurationTestFinishCount = async (): Promise<number> => {
  try {
    const params = new URLSearchParams({
      page: '1',
      per_page: '1',
      not_in_service: '0',
      self_test: '0',
      duration_test_status: DurationTestStatus.DurationTestVerify.toString(),
    })

    const response = await samApiClient.get(
      `duration-test-reports?${params.toString()}`,
    )
    return Number(response.headers['x-total-count']) || 0
  } catch (error) {
    console.error('Failed to fetch duration test finish count:', error)
    return 0
  }
}
